import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { FirebaseError } from 'firebase/app';
import { AuthErrorCodes } from 'firebase/auth';
import { BaseComponent } from '@core/base.component';
import { TestHooks } from './forgot.hooks';
import { Logger } from '@core/logger';

enum USER_CREDENTIALS {
	EMAIL = 'email',
}

interface VM {
	loading: boolean;
}

@Component({
	selector: 'app-forgot',
	templateUrl: './forgot.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ForgotComponent extends BaseComponent<VM> {
	public testHooks = TestHooks;
	public USER_CREDENTIALS = USER_CREDENTIALS;
	public authForm = this.fb.group({
		[USER_CREDENTIALS.EMAIL]: ['', [Validators.required, Validators.email]],
	});

	constructor(
		private auth: AngularFireAuth,
		private fb: FormBuilder,
		private navigationService: NavigationService,
		private snackbarService: SnackbarService,
	) {
		super({ loading: false });
	}

	get registerUrl() {
		return UrlService.SignUp();
	}

	async resetPassword() {
		if (this.authForm.invalid) return;
		this.VM = { loading: true };

		try {
			await this.auth.sendPasswordResetEmail(
				this.authForm.value[USER_CREDENTIALS.EMAIL],
			);
			this.snackbarService.openSnackBar({
				text: 'If the email exists, you will have received an email. Please check your inbox.',
				type: 'success',
			});
			this.navigationService.goToLogin();
		} catch (err) {
			if (
				err instanceof FirebaseError &&
				err.code === AuthErrorCodes.USER_DELETED
			) {
				this.snackbarService.openSnackBar({
					text: 'No user with this email address was found. Please try another email.',
					type: 'error',
				});
			} else {
				Logger.error('Unable to send reset password');
				Logger.exception(err, {
					email: this.authForm.value[USER_CREDENTIALS.EMAIL],
				});

				this.snackbarService.openSnackBar({
					text: 'Unable to send reset password. Please try again',
					type: 'error',
				});
			}
		} finally {
			this.VM = { loading: false };
		}
	}
}
